import { useMemo, Dispatch, SetStateAction } from 'react';
import {
  SpaceBetween,
  Container,
  Select,
  Checkbox,
  CheckboxProps,
  Textarea,
  Input,
  Multiselect,
} from '@amzn/awsui-components-react';
import FormField from '@amzn/awsui-components-react/polaris/form-field';
import Button from '@amzn/awsui-components-react/polaris/button';
import { useTranslation } from 'react-i18next';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import { NonCancelableEventHandler } from '@amzn/awsui-components-react/polaris/internal/events';
import { ambassadorRegistrationPossibleCountries } from '../../data/ambassadorRegistrationOptions';
import { siteCodeOptions } from '../../data/siteCodes';
import { elgs } from '../../data/elgs';

export interface AmbassadorRegistrationFormValues {
  country: OptionDefinition | null;
  city: OptionDefinition | null;
  siteCode: OptionDefinition | null;
  businessUnit: OptionDefinition | null;
  siteRepresentation: boolean;
  teamRepresentation: boolean;
  employeeLedGroupRepresentation: boolean;
  howDidYouHear: OptionDefinition | null;
  howDidYouHearSpecification: string;
  myManagerHasApprovedThisRole: boolean;
  sendMeACopy: boolean;
  teamLocation: OptionDefinition[];
  teamName?: string | undefined;
  ergName: OptionDefinition | null;
  ergLocation: OptionDefinition[];
  representMultipleSites: boolean;
  representedSiteCodes: OptionDefinition[];
}

export interface AmbassadorRegistrationFormProps {
  formValues: AmbassadorRegistrationFormValues;
  setFormValues: Dispatch<SetStateAction<AmbassadorRegistrationFormValues>>;
  onSubmit: () => void;
  loading: boolean;
  isUpdateForm?: boolean;
}

export function AmbassadorRegistrationForm({
  formValues,
  setFormValues,
  onSubmit,
  loading,
  isUpdateForm,
}: AmbassadorRegistrationFormProps) {
  const { t } = useTranslation(['ambassadors', 'translation']);

  const howDidYouHearOptions = [
    { label: t('ambassadorRegistration.ACIWiki'), value: '1' },
    { label: t('ambassadorRegistration.togetherAtAmazon'), value: '2' },
    { label: t('ambassadorRegistration.existingCommunityAmbassadorReferral'), value: '3' },
    { label: t('ambassadorRegistration.internalcommunications'), value: '4' },
    { label: t('ambassadorRegistration.aToZ'), value: '5' },
    { label: t('ambassadorRegistration.leadershipRecommendation'), value: '6' },
    { label: t('ambassadorRegistration.otherPleaseSpecify'), value: '7' },
  ];

  const isValid = useMemo(() => {
    // one of the representation boxes is chosen
    // and the vlaues for the chosen representations are filled out
    const representationChosen =
      (formValues.siteRepresentation ||
        formValues.teamRepresentation ||
        formValues.employeeLedGroupRepresentation) &&
      (formValues.siteRepresentation || !formValues.siteRepresentation) &&
      ((formValues.teamRepresentation && formValues.teamLocation?.length && formValues.teamName) ||
        !formValues.teamRepresentation) &&
      ((formValues.employeeLedGroupRepresentation &&
        formValues.ergName &&
        formValues.ergLocation?.length) ||
        !formValues.employeeLedGroupRepresentation);

    return (
      representationChosen &&
      ((formValues.myManagerHasApprovedThisRole &&
        formValues.howDidYouHear?.label &&
        !isUpdateForm) ||
        isUpdateForm)
    );
  }, [formValues, isUpdateForm]);

  const submitForm = async () => {
    onSubmit();
  };

  return (
    <>
      <SpaceBetween direction="vertical" size="m">
        <Container>
          <SpaceBetween direction="vertical" size="m">
            <SpaceBetween direction="vertical" size="xxxs">
              <h2
                data-testid={`ambassador-${isUpdateForm ? 'update' : 'registration'}-representation-header`}
                style={{ marginBottom: 0 }}
              >
                {t('ambassadorRegistration.representation')}
              </h2>
              <span>{t('ambassadorRegistration.moreThanOne')}</span>
            </SpaceBetween>

            <RepresentationCheckbox
              checked={formValues.siteRepresentation}
              onChange={({ detail }) =>
                setFormValues((fv) => ({ ...fv, siteRepresentation: detail.checked }))
              }
              title={t('ambassadorRegistration.site')}
              description={t('ambassadorRegistration.siteDescription')}
              formValues={formValues}
              setFormValues={setFormValues}
              controlId="ambassador-registration-site-representation-checkbox"
            />
            <RepresentationCheckbox
              checked={formValues.teamRepresentation}
              onChange={({ detail }) =>
                setFormValues((fv) => ({ ...fv, teamRepresentation: detail.checked }))
              }
              title={t('ambassadorRegistration.team')}
              description={t('ambassadorRegistration.teamDescription')}
              formValues={formValues}
              setFormValues={setFormValues}
              controlId="ambassador-registration-team-representation-checkbox"
            />
            <RepresentationCheckbox
              checked={formValues.employeeLedGroupRepresentation}
              onChange={({ detail }) =>
                setFormValues((fv) => ({ ...fv, employeeLedGroupRepresentation: detail.checked }))
              }
              title={t('ambassadorRegistration.employeeLedGroup')}
              description={t('ambassadorRegistration.employeeLedGroupDescription')}
              formValues={formValues}
              setFormValues={setFormValues}
            />
            {!isUpdateForm && (
              <>
                <h3>{t('ambassadorRegistration.howDidYouHear')}</h3>
                <FormField stretch>
                  <Select
                    selectedOption={formValues.howDidYouHear}
                    placeholder={t('ambassadorRegistration.selectOption')}
                    data-testid="ambassador-registration-heard-about-select-input"
                    onChange={({ detail }) =>
                      setFormValues((fv) => ({ ...fv, howDidYouHear: detail.selectedOption }))
                    }
                    options={howDidYouHearOptions}
                  />
                </FormField>
                {formValues.howDidYouHear?.label ===
                  t('ambassadorRegistration.otherPleaseSpecify') && (
                  <Textarea
                    onChange={({ detail }) =>
                      setFormValues((fv) => ({ ...fv, howDidYouHearSpecification: detail.value }))
                    }
                    value={formValues.howDidYouHearSpecification}
                    placeholder={t('ambassadorRegistration.pleaseSpecify')}
                    data-testid="ambassador-registration-specify-other-way-to-hear"
                  />
                )}
                <Checkbox
                  onChange={({ detail }) =>
                    setFormValues((fv) => ({ ...fv, myManagerHasApprovedThisRole: detail.checked }))
                  }
                  checked={formValues.myManagerHasApprovedThisRole}
                  controlId="ambassador-registration-manager-approved-checkbox"
                >
                  {t('ambassadorRegistration.myManagerHasApprovedThisRole')}
                </Checkbox>
              </>
            )}
            <Checkbox
              onChange={({ detail }) =>
                setFormValues((fv) => ({ ...fv, sendMeACopy: detail.checked }))
              }
              checked={formValues.sendMeACopy}
            >
              {t('ambassadorRegistration.sendMeACopy')}
            </Checkbox>
          </SpaceBetween>
        </Container>

        <div>
          <Button
            variant="primary"
            disabled={!isValid}
            loading={loading}
            onClick={submitForm}
            data-testid={`ambassador-registration-${isUpdateForm ? 'save' : 'submit'}-button`}
          >
            {isUpdateForm
              ? t('ambassadorRegistration.save')
              : t('ambassadorRegistration.submitApplication')}
          </Button>
        </div>
      </SpaceBetween>
    </>
  );
}

export const RepresentationCheckbox = ({
  checked,
  title,
  description,
  onChange,
  formValues,
  setFormValues,
  controlId,
}: {
  checked: boolean;
  title: string;
  description: string;
  onChange: NonCancelableEventHandler<CheckboxProps.ChangeDetail> | undefined;
  formValues: AmbassadorRegistrationFormValues;
  setFormValues: Dispatch<SetStateAction<AmbassadorRegistrationFormValues>>;
  controlId?: string;
}) => {
  const { t } = useTranslation(['ambassadors', 'translation']);

  return (
    <FormField stretch>
      <div
        className="ambassador-representation-checkbox"
        data-testid="ambassador-representation-checkbox"
      >
        <SpaceBetween size="s" direction="vertical">
          <Checkbox
            onChange={onChange}
            checked={checked}
            description={description}
            data-testid={`representation-checkbox-open=${checked}`}
            controlId={controlId}
          >
            {title}
          </Checkbox>
          {checked && title === t('ambassadorRegistration.site') && (
            <>
              <Checkbox
                onChange={({ detail }) =>
                  setFormValues((fv) => ({ ...fv, representMultipleSites: detail.checked }))
                }
                checked={formValues.representMultipleSites}
              >
                {t('ambassadorRegistration.representMultipleSites')}
              </Checkbox>
              {formValues.representMultipleSites && (
                <Multiselect
                  onChange={({ detail }) =>
                    setFormValues((fv) => ({
                      ...fv,
                      representedSiteCodes: [...detail.selectedOptions],
                    }))
                  }
                  selectedOptions={formValues.representedSiteCodes}
                  options={siteCodeOptions}
                  inlineTokens
                  filteringType="auto"
                  virtualScroll
                  placeholder={t('ambassadorRegistration.selectOption')}
                />
              )}
            </>
          )}
          {checked && title === t('ambassadorRegistration.team') && (
            <>
              <FormField stretch label={t('ambassadorRegistration.teamLocation')}>
                <Multiselect
                  onChange={({ detail }) =>
                    setFormValues((fv) => ({ ...fv, teamLocation: [...detail.selectedOptions] }))
                  }
                  selectedOptions={formValues.teamLocation}
                  options={ambassadorRegistrationPossibleCountries}
                  filteringType="auto"
                  virtualScroll
                  placeholder={t('ambassadorRegistration.selectOption')}
                  data-testid="ambassador-team-representation-country-multi-select"
                />
              </FormField>
              <FormField stretch label={t('ambassadorRegistration.teamName')}>
                <Input
                  onChange={({ detail }) =>
                    setFormValues((fv) => ({ ...fv, teamName: detail.value }))
                  }
                  value={formValues.teamName || ''}
                  placeholder={t('ambassadorRegistration.teamName')}
                  data-testid="ambassador-team-representation-name-input"
                />
              </FormField>
            </>
          )}
          {checked && title === t('ambassadorRegistration.employeeLedGroup') && (
            <>
              <FormField stretch label={t('ambassadorRegistration.ergName')}>
                <Select
                  selectedOption={formValues.ergName}
                  placeholder={t('ambassadorRegistration.ergName')}
                  onChange={({ detail }) =>
                    setFormValues((fv) => ({ ...fv, ergName: detail.selectedOption }))
                  }
                  options={elgs}
                />
              </FormField>
              <FormField stretch label={t('ambassadorRegistration.ergLocation')}>
                <Multiselect
                  onChange={({ detail }) =>
                    setFormValues((fv) => ({ ...fv, ergLocation: [...detail.selectedOptions] }))
                  }
                  selectedOptions={formValues.ergLocation}
                  options={ambassadorRegistrationPossibleCountries}
                  filteringType="auto"
                  virtualScroll
                  placeholder={t('ambassadorRegistration.selectOption')}
                />
              </FormField>
            </>
          )}
        </SpaceBetween>
      </div>
    </FormField>
  );
};
